import type { MachineLimits, UsageSetting, UsageTotals } from '@shared/types/factory';
import type { ReportAgentLimits, ReportLimitPace, ReportLimitWindow } from '@shared/types/machine-report';
import { EMPTY } from './empty';
import { formatNumber } from './formatters';

/**
 * LLM usage (T-12) in words: agent time, token counts, model settings, and the agents' plan limits.
 * Shared by the task and run summaries, the Usage page and the Fleet pages.
 */

const MIN = 60_000;

/** Agent time: "under 1m", "45m", "3.2h". */
export function formatAgentTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return EMPTY;
  if (ms < MIN) return ms > 0 ? 'under 1m' : '0m';
  const mins = ms / MIN;
  if (mins < 60) return `${formatNumber(Math.round(mins))}m`;
  return `${formatNumber(mins / 60, { maximumFractionDigits: 1 })}h`;
}

/** A token count, short: "950", "12k", "1.4M". */
export function formatTokens(n: number | null | undefined): string {
  if (n === null || n === undefined) return EMPTY;
  return formatNumber(n, { notation: 'compact', maximumFractionDigits: n >= 1e6 ? 1 : 0 });
}

/** "claude-opus-5-5" → "Opus 5.5"; "claude-haiku-5" → "Haiku 5"; anything else as it is. */
export function modelName(model: string): string {
  const full = /^claude-([a-z]+)-(\d+)-(\d+)/.exec(model);
  if (full) return `${full[1]![0]!.toUpperCase()}${full[1]!.slice(1)} ${full[2]}.${full[3]}`;
  const major = /^claude-([a-z]+)-(\d+)$/.exec(model);
  if (major) return `${major[1]![0]!.toUpperCase()}${major[1]!.slice(1)} ${major[2]}`;
  return model;
}

/** "Opus 5.5 · high · fast"; the default effort shows as "default effort". */
export function settingLabel(s: Pick<UsageSetting, 'model' | 'effort' | 'speed'>): string {
  return [modelName(s.model), s.effort === 'default' ? 'default effort' : s.effort, s.speed].join(' · ');
}

/** The share of input tokens read from the cache, 0–100; null without input counts. */
export function cachedShare(t: Pick<UsageTotals, 'input_tokens' | 'cached_input_tokens'>): number | null {
  if (!t.input_tokens || t.cached_input_tokens === null) return null;
  return Math.min(100, (t.cached_input_tokens / t.input_tokens) * 100);
}

// ---- Plan limits ----

export type LimitTone = 'success' | 'warning' | 'danger';

/** How worried to be about a window: over 95% or running out before it resets is danger; over 80% a warning. */
export function limitTone(usedPercent: number | null | undefined, pace?: ReportLimitPace | null): LimitTone {
  const used = usedPercent ?? 0;
  if (used >= 95) return 'danger';
  if (pace?.runsOutAt) return used >= 50 ? 'danger' : 'warning';
  if (used >= 80) return 'warning';
  return 'success';
}

export interface TightestLimit {
  agent: Pick<ReportAgentLimits, 'id' | 'name'>;
  window: ReportLimitWindow;
}

/** The fullest window across a machine's agents (ties: the one that resets last); null with none read. */
export function tightestLimit(agents: readonly ReportAgentLimits[] | null | undefined): TightestLimit | null {
  let best: TightestLimit | null = null;
  for (const agent of agents ?? []) {
    if (agent.error) continue;
    for (const window of agent.windows ?? []) {
      if (window.usedPercent === null || window.usedPercent === undefined) continue;
      const cur = best?.window;
      if (
        !cur ||
        window.usedPercent > (cur.usedPercent ?? -1) ||
        (window.usedPercent === cur.usedPercent && (window.resetsAt ?? '') > (cur.resetsAt ?? ''))
      ) {
        best = { agent: { id: agent.id, name: agent.name }, window };
      }
    }
  }
  return best;
}

/** "Claude Weekly 62%", for the Fleet list. */
export function tightestLimitLabel(limits: MachineLimits | null | undefined): string | null {
  const t = tightestLimit(limits?.agents);
  if (!t) return null;
  return `${t.agent.name} ${t.window.label} ${formatNumber(t.window.usedPercent, { maximumFractionDigits: 0 })}%`;
}
